import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import MarkdownTextarea from '../inputs/MarkdownTextarea';
import { Label, Error, Hint } from '@progress/kendo-react-labels';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A markdown form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<MarkdownTextarea>} MarkdownTextareaProps
 *
 * @param {BaseFieldRenderProps & MarkdownTextareaProps} fieldRenderProps
 * @returns {React.ReactElement} Markdown form field.
 */
const FormMarkdownTextarea = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    hint,
    optional,
    wrapperClassName,
    Wrapper = FieldWrapper,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';

  return (
    <Wrapper className={wrapperClassName}>
      {label && (
        <Label
          editorId={editorId}
          editorValid={valid}
          optional={optional}
        >
          {label}
        </Label>
      )}
      <MarkdownTextarea
        valid={valid}
        id={editorId}
        name={name}
        ariaDescribedBy={`${hintId} ${errorId}`}
        dataTestId={editorId}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </Wrapper>
  );
};

export default FormMarkdownTextarea;
